import { backendUrl, backendHeaders } from "./backend";

export const visitApi = {
	profile: "amc_management.api.vendor_portal_api.get_vendor_profile",
	dashboard: "amc_management.api.vendor_portal_api.get_visit_dashboard",
	contracts: "amc_management.api.vendor_portal_api.get_vendor_contracts",
	contractDetail: "amc_management.api.vendor_portal_api.get_vendor_contract_detail",
	invoices: "amc_management.api.vendor_portal_api.get_vendor_invoices",
	invoiceDetail: "amc_management.api.vendor_portal_api.get_vendor_invoice_detail",
	submitInvoice: "amc_management.api.vendor_portal_api.submit_vendor_invoice",
	soaEntries: "amc_management.api.vendor_portal_api.get_vendor_soa_entries",
	uploadSOA: "amc_management.api.vendor_portal_api.upload_vendor_soa",
	downloadSOA: "amc_management.api.vendor_portal_api.download_vendor_soa",
	users: "amc_management.api.vendor_portal_api.get_vendor_portal_users",
	createUser: "amc_management.api.vendor_portal_api.create_vendor_portal_user",
	properties: "amc_management.api.vendor_portal_api.get_vendor_properties",
	visits: "amc_management.api.vendor_portal_api.get_visits",
	visitDetail: "amc_management.api.vendor_portal_api.get_visit_detail",
	startVisit: "amc_management.api.vendor_portal_api.start_visit",
	completeVisit: "amc_management.api.vendor_portal_api.complete_visit",
	rectificationCategories:
		"amc_management.api.vendor_portal_api.get_rectification_issue_categories",
	raiseRectification: "amc_management.api.vendor_portal_api.raise_visit_rectification",
	raiseRectifications: "amc_management.api.vendor_portal_api.raise_visit_rectifications",
	updateRectification: "amc_management.api.vendor_portal_api.update_visit_rectification",
	downloadRectificationAttachment:
		"amc_management.api.vendor_portal_api.download_vendor_rectification_attachment",
	addAttachment: "amc_management.api.vendor_portal_api.add_visit_attachment",
	removeAttachment: "amc_management.api.vendor_portal_api.remove_visit_attachment",
};

export const routePermissions = {
	Dashboard: "schedules",
	Visits: "schedules",
	VisitDetail: "schedules",
	RaiseRectification: "schedules",
	Contracts: "contracts",
	ContractDetail: "contracts",
	Invoices: "invoices",
	InvoiceDetail: "invoices",
	SOA: "soa",
	Users: "manage_users",
	Profile: null,
};

export function hasPermission(permissions, permission) {
	if (!permission) return true;
	return (permissions || []).includes(permission);
}

export function canAccessRoute(routeName, permissions) {
	return hasPermission(permissions, routePermissions[routeName]);
}

export function defaultRouteForPermissions(permissions) {
	if (hasPermission(permissions, "schedules")) return "Dashboard";
	if (hasPermission(permissions, "contracts")) return "Contracts";
	if (hasPermission(permissions, "invoices")) return "Invoices";
	if (hasPermission(permissions, "soa")) return "SOA";
	if (hasPermission(permissions, "manage_users")) return "Users";
	return "Profile";
}

export function formatDate(value) {
	if (!value) return "-";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;
	return date.toLocaleDateString("en-IN", {
		day: "2-digit",
		month: "short",
		year: "numeric",
	});
}

function parseLocalDate(value) {
	if (value instanceof Date) {
		return Number.isNaN(value.getTime())
			? null
			: new Date(value.getFullYear(), value.getMonth(), value.getDate());
	}

	const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
	if (!match) return null;

	const [, year, month, day] = match.map(Number);
	const date = new Date(year, month - 1, day);
	if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
		return null;
	}
	return date;
}

function localDateKey(date) {
	return [date.getFullYear(), date.getMonth() + 1, date.getDate()]
		.map((part) => String(part).padStart(2, "0"))
		.join("-");
}

export function getTolerancePeriod(value) {
	const toleranceDays = Number(value);
	return Number.isInteger(toleranceDays) && toleranceDays >= 0 ? toleranceDays : 0;
}

export function getToleranceWindow(visit) {
	const expectedDate = parseLocalDate(visit?.expected_visit_date);
	const toleranceDays = getTolerancePeriod(visit?.tolerance_period);
	if (!expectedDate) {
		return {
			expectedDate: null,
			startDate: null,
			endDate: null,
			toleranceDays,
		};
	}

	const startDate = new Date(expectedDate);
	startDate.setDate(startDate.getDate() - toleranceDays);
	const endDate = new Date(expectedDate);
	endDate.setDate(endDate.getDate() + toleranceDays);

	return {
		expectedDate: localDateKey(expectedDate),
		startDate: localDateKey(startDate),
		endDate: localDateKey(endDate),
		toleranceDays,
	};
}

export function getVisitPresentation(visit, today = new Date()) {
	const window = getToleranceWindow(visit);
	const todayDate = parseLocalDate(today);
	const todayKey = todayDate ? localDateKey(todayDate) : null;
	const normalizedStatus = String(visit?.status || "").toLowerCase();
	const isCompleted = normalizedStatus === "completed";
	const isBackendSkipped = normalizedStatus === "skipped";
	const isPastTolerance = Boolean(window.endDate && todayKey && todayKey > window.endDate);
	const isSkipped = isBackendSkipped || (!isCompleted && isPastTolerance);

	return {
		...window,
		isPastTolerance,
		isSkipped,
		canOpen: !isSkipped,
		displayStatus: isSkipped ? "Contact coordinator" : visit?.status || "Unknown",
	};
}

export function compactDate(value) {
	if (!value) return "-";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;
	return date.toLocaleDateString("en-IN", {
		day: "2-digit",
		month: "short",
	});
}

export function statusTone(status) {
	const normalized = (status || "").toLowerCase();
	if (normalized === "completed") return "green";
	if (normalized === "in progress") return "blue";
	if (normalized === "over due" || normalized === "overdue") return "red";
	if (normalized === "contact coordinator") return "orange";
	if (normalized === "skipped") return "red";
	if (normalized === "scheduled") return "orange";
	if (normalized === "hold") return "gray";
	return "gray";
}

export async function uploadPrivateFile(file, { doctype, docname } = {}) {
	const formData = new FormData();
	formData.append("file", file);
	formData.append("is_private", "1");
	if (doctype) formData.append("doctype", doctype);
	if (docname) formData.append("docname", docname);

	const headers = await backendHeaders();

	const response = await fetch(backendUrl("/api/method/upload_file"), {
		credentials: "include",
		method: "POST",
		body: formData,
		headers,
	});
	const data = await response.json().catch(() => ({}));
	if (!response.ok || data.exc) {
		throw new Error(data._error_message || data.message || "File upload failed");
	}
	return data.message;
}
